import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "@/components/Header";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { CheckCircle2, Circle, XCircle, Clock, Filter, History, ChevronDown, ChevronUp, RefreshCw, AlertTriangle } from "lucide-react";

const FILTERS = [
  ["all", "All Moves"], ["committed", "Committed"], ["awaiting", "Awaiting Outcome"],
  ["needs_action", "Needs Action"], ["resolved", "Resolved"], ["didnt", "Didn't Act"],
];
const CATEGORIES = ["Pricing & Monetisation", "Marketing & Acquisition", "Sales & Conversion", "Product & UX", "Positioning & Brand", "Operations & Productivity", "Customer Experience", "Strategy & Prioritisation", "Freelance / Client Management", "Business Model"];

const STATUS = {
  completed: { label: "Completed", color: "#10B981", Icon: CheckCircle2 },
  partial: { label: "Partly done", color: "#F59E0B", Icon: Circle },
  committed: { label: "Awaiting outcome", color: "#FF6B35", Icon: Clock },
  reconsider: { label: "Reconsidering", color: "#A0A6B2", Icon: RefreshCw },
  recommended: { label: "Not acted", color: "#656C7B", Icon: Circle },
  needs_action: { label: "Needs action", color: "#FB7185", Icon: AlertTriangle },
  didnt: { label: "Didn't act", color: "#F43F5E", Icon: XCircle },
};

export default function MyMoves() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");
  const [category, setCategory] = useState("all");
  const [moves, setMoves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    setLoading(true);
    const params = { filter };
    if (category !== "all") params.category = category;
    api.get("/moves", { params })
      .then(({ data }) => setMoves(Array.isArray(data?.moves) ? data.moves : []))
      .catch(() => setMoves([]))
      .finally(() => setLoading(false));
  }, [filter, category]);

  return (
    <div className="min-h-screen">
      <Header />
      <main className="mx-auto max-w-5xl px-5 py-10 sm:px-8">
        <span className="eyebrow">Decisions & actions — not conversations</span>
        <h1 className="font-head mt-2 text-4xl font-black tracking-tight text-white">My Moves</h1>
        <p className="mt-2 text-neutral-400">One card per decision. Rephrasing the same blocker adds to its history — it doesn't create a new Move.</p>

        <div className="mt-8 flex flex-wrap items-center gap-2">
          {FILTERS.map(([v, label]) => (
            <button key={v} data-testid={`filter-${v}`} onClick={() => setFilter(v)}
              className={`rounded-full border px-4 py-1.5 text-sm transition-all ${filter === v ? "border-[var(--amber)] bg-[var(--amber)]/10 text-white" : "border-white/12 text-neutral-400 hover:border-white/30"}`}>
              {label}
            </button>
          ))}
          <div className="ml-auto">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="filter-category" className="w-[210px] border-white/12 bg-[var(--surface-raised)] text-white">
                <Filter className="mr-1 h-3.5 w-3.5" /><SelectValue />
              </SelectTrigger>
              <SelectContent className="border-white/12 bg-[var(--surface-overlay)] text-white">
                <SelectItem value="all">All categories</SelectItem>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-8 space-y-4">
          {loading && <p className="text-neutral-500">Loading…</p>}
          {!loading && moves.length === 0 && (
            <div className="rounded-2xl border border-white/10 bg-[var(--surface)] p-10 text-center" data-testid="moves-empty-state">
              <p className="font-head text-xl font-extrabold text-white">{filter === "all" && category === "all" ? "No moves yet." : "No moves match this filter."}</p>
              <p className="mt-1.5 text-sm text-neutral-400">{filter === "all" && category === "all" ? "Your first decision starts here." : "Try a different filter or clear a new blocker."}</p>
              <button data-testid="moves-empty-cta" onClick={() => navigate("/app")} className="mt-4 text-sm text-[var(--amber)] hover:underline">Clear your first blocker →</button>
            </div>
          )}
          {moves.map((m, i) => {
            const st = STATUS[m.status] || STATUS.recommended;
            return (
              <motion.div key={m.thread_id || m.blocker_id || i} data-testid={`move-card-${i}`}
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.03 * i }}
                className="rounded-2xl border border-white/10 bg-[var(--surface)] p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <span className="font-mono2 text-[10px] uppercase tracking-wider text-neutral-500">{m.category}</span>
                    <p className="mt-1.5 text-[15px] font-semibold text-white">{m.text}</p>
                  </div>
                  <span className="flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium" style={{ color: st.color, background: st.color + "14" }}>
                    <st.Icon className="h-3.5 w-3.5" /> {st.label}
                  </span>
                </div>
                {(m.reconsideration_count > 0 || m.history?.length > 0 || m.changed_contexts?.length > 0) && (
                  <div className="mt-3 flex flex-wrap items-center gap-2" data-testid={`move-thread-${i}`}>
                    {m.reconsideration_count > 0 && m.advice_meter_state !== "decision_avoidance" && (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-white/12 px-2.5 py-1 font-mono2 text-[10px] uppercase tracking-wider text-neutral-400">
                        <RefreshCw className="h-3 w-3" /> {m.reconsideration_count} {m.reconsideration_count === 1 ? "reconsideration" : "reconsiderations"}
                      </span>
                    )}
                    {m.changed_contexts?.length > 0 && (
                      <span className="rounded-full border border-[var(--emerald)]/40 bg-[var(--emerald)]/10 px-2.5 py-1 font-mono2 text-[10px] uppercase tracking-wider text-[var(--emerald)]" data-testid={`move-reopened-${i}`}>
                        Circumstances changed · {m.history?.length || 0} earlier {m.history?.length === 1 ? "version" : "versions"}
                      </span>
                    )}
                    {m.advice_meter_state === "decision_avoidance" && (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-rose-500/40 bg-rose-500/10 px-2.5 py-1 font-mono2 text-[10px] uppercase tracking-wider text-rose-400" data-testid={`move-avoidance-${i}`}>
                        <AlertTriangle className="h-3 w-3" /> Decision avoidance · {m.reconsideration_count} {m.reconsideration_count === 1 ? "reconsideration" : "reconsiderations"}
                      </span>
                    )}
                    {m.advice_meter_state === "loop_forming" && (
                      <span className="rounded-full border border-[var(--amber)]/40 bg-[var(--amber)]/10 px-2.5 py-1 font-mono2 text-[10px] uppercase tracking-wider text-[var(--amber)]">Loop forming</span>
                    )}
                    {m.history?.length > 0 && (
                      <button data-testid={`move-history-toggle-${i}`} onClick={() => setExpanded((e) => ({ ...e, [m.thread_id]: !e[m.thread_id] }))}
                        className="ml-auto inline-flex items-center gap-1 text-xs text-neutral-400 hover:text-white">
                        <History className="h-3.5 w-3.5" /> {expanded[m.thread_id] ? "Hide" : "View"} decision history
                        {expanded[m.thread_id] ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                      </button>
                    )}
                  </div>
                )}
                {m.recommendation && (
                  <div className="mt-4 grid gap-3 border-t border-white/8 pt-4 sm:grid-cols-2">
                    <div><span className="font-mono2 text-[10px] uppercase tracking-wider text-[var(--amber)]">{m.reconsideration_count > 0 ? "Current move" : "Next move"}</span><p className="mt-1 text-sm text-neutral-300">{m.recommendation.do_this}</p></div>
                    <div><span className="font-mono2 text-[10px] uppercase tracking-wider text-[var(--emerald)]">First action</span><p className="mt-1 text-sm text-neutral-400">{m.recommendation.next_15_minutes}</p></div>
                  </div>
                )}
                {expanded[m.thread_id] && (
                  <div className="mt-4 space-y-3 border-t border-white/8 pt-4" data-testid={`move-history-${i}`}>
                    <span className="eyebrow">Decision history · earlier versions of this blocker</span>
                    {[...m.history].reverse().map((h, j) => (
                      <div key={j} className="rounded-xl border border-white/8 bg-[var(--surface-raised)]/60 px-4 py-3">
                        <p className="text-xs text-neutral-500">“{h.blocker_text}”</p>
                        <p className="mt-1.5 text-sm text-neutral-300">{h.recommendation?.do_this}</p>
                      </div>
                    ))}
                    {m.changed_contexts?.map((c, j) => (
                      <p key={`c${j}`} className="text-xs text-[var(--emerald)]">Circumstances changed: “{c.reason}”</p>
                    ))}
                  </div>
                )}
                {m.outcome && (m.outcome.note || m.outcome.money_value || m.outcome.minutes_saved) && (
                  <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-white/8 pt-3 text-xs text-neutral-400">
                    {m.outcome.note && <span>“{m.outcome.note}”</span>}
                    {m.outcome.minutes_saved ? <Badge variant="outline" className="border-white/12" data-testid={`move-time-${i}`}>{m.outcome.minutes_saved_label || `${m.outcome.minutes_saved}m`} saved</Badge> : null}
                    {m.outcome.money_value ? <Badge variant="outline" className="border-white/12 text-[var(--emerald)]" data-testid={`move-money-${i}`}>£{m.outcome.money_value} user-reported</Badge> : null}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
